import versionJson from './version.json'
import React from 'react'
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  RouterProvider
} from 'react-router'
import { BASE_URL } from './constants'
import {
  AuthProvider,
  ConfirmUpdateModal,
  ErrorScreen,
  LoadingScreen,
  LoginScreen,
  PrivateOutlet,
  RoleGuard,
  AccessDeniedScreen,
  SchemaScreen,
  ThemeProvider,
  useAutoUpdater
} from 'prove-common'
import { SettingsScreen } from './screens/SettingsScreen'
import { HomeScreen } from './screens/HomeScreen'
import { NewsScreen } from './screens/NewsScreen'
import { NewsEditorScreen } from './screens/NewsEditorScreen'
import { NewsArticleScreen } from './screens/NewsArticleScreen'
import { SchemasScreen } from './screens/SchemasScreen'
import { LabsScreen } from './screens/LabsScreen'
import { LabScreen } from './screens/LabScreen'
import { ProductsScreen } from './screens/ProductsScreen'
import { MatricesScreen } from './screens/MatricesScreen'
import { OkProgramScreen } from './screens/OkProgramScreen'
import { PurposesScreen } from './screens/PurposesScreen'
import { SubPurposesScreen } from './screens/SubPurposesScreen'
import { SubstancesScreen } from './screens/SubstancesScreen'
import { SchemaScreenTopBar } from './components/schema-screen-top-bar/SchemaScreenTopBar'
import { AppLogo } from './components/app-logo/AppLogo'
import { CodeGeneratorScreen } from './screens/CodeGeneratorScreen'
import { SamplingSitesScreen } from './screens/SamplingSitesScreen'
import { RedirectRequest } from '@azure/msal-browser'
import { ResultsUploadScreen } from './screens/ResultsUploadScreen'
import { TeamsScreen } from './screens/TeamsScreen'
import { RouteMetadata } from './components/RouteMetadata'
import { NotFoundScreen } from './screens/NotFoundScreen'
import { UserPermissionsScreen } from './screens/UserPermissionsScreen'
import { EditSampleScreen } from './screens/EditSampleScreen'

const routes: RouteObject[] = [
  {
    element: <RouteMetadata />,
    errorElement: <ErrorScreen logo={<AppLogo />} />,
    children: [
      {
        path: '/login',
        element: (
          <LoginScreen
            logo={<AppLogo />}
            loginButtonText={'Logg inn'}
            homeUrl='/home'
          />
        )
      },
      {
        path: '/unauthorized',
        element: <AccessDeniedScreen />
      },
      {
        element: (
          <PrivateOutlet
            homeUrl='/home'
            loadingElement={
              <LoadingScreen
                loadingMessage='Laster inn...'
                logo={<AppLogo />}
              />
            }
            errorElement={<ErrorScreen logo={<AppLogo />} />}
          />
        ),
        path: '/',
        children: [
          {
            element: <RoleGuard requiredRoles={['admin']} />,
            children: [
              {
                index: true,
                element: <Navigate to='/home' replace />
              },
              {
                path: '/settings',
                element: <SettingsScreen />
              },
              {
                path: '/home',
                element: <HomeScreen />
              },
              {
                path: '/code-generator',
                element: <CodeGeneratorScreen />
              },
              {
                path: '/news',
                element: <NewsScreen />
              },
              {
                path: '/news/new',
                element: <NewsEditorScreen />
              },
              {
                path: '/news/:newsId/edit',
                element: <NewsEditorScreen />
              },
              {
                path: '/news/:newsId',
                element: <NewsArticleScreen />
              },
              {
                path: '/schemas',
                element: <SchemasScreen />
              },
              {
                path: '/schemas/:payloadSchemaId',
                element: <SchemaScreen topBar={<SchemaScreenTopBar />} />
              },
              {
                path: '/labs',
                element: <LabsScreen />
              },
              {
                path: '/labs/:labId',
                element: <LabScreen />
              },
              {
                path: '/products',
                element: <ProductsScreen />
              },
              {
                path: '/matrices',
                element: <MatricesScreen />
              },
              {
                path: '/ok-programs',
                element: <OkProgramScreen />
              },
              {
                path: '/purposes',
                element: <PurposesScreen />
              },
              {
                path: '/sub-purposes',
                element: <SubPurposesScreen />
              },
              {
                path: '/substances',
                element: <SubstancesScreen />
              },
              {
                path: '/results-upload',
                element: <ResultsUploadScreen />
              },
              {
                path: '/sampling-sites',
                element: <SamplingSitesScreen />
              },
              {
                path: '/teams',
                element: <TeamsScreen />
              },
              {
                path: '/user-permissions',
                element: <UserPermissionsScreen />
              },
              {
                path: '/edit-sample',
                element: <EditSampleScreen />
              },
              {
                path: '/edit-sample/:sampleId',
                element: <EditSampleScreen />
              },
              {
                path: '*',
                element: <NotFoundScreen />
              }
            ]
          }
        ]
      }
    ]
  }
]
const browserRouter = createBrowserRouter(routes)

export default function App({
  loginRequest
}: Readonly<{
  loginRequest: RedirectRequest
}>) {
  const { updateAvailable } = useAutoUpdater(versionJson)
  return (
    <AuthProvider loginRequest={loginRequest} baseUrl={BASE_URL}>
      <ThemeProvider>
        <RouterProvider router={browserRouter} />
        <ConfirmUpdateModal isOpen={updateAvailable} />
      </ThemeProvider>
    </AuthProvider>
  )
}
